/**
 * Request Interceptor
 * Tüm giden request'leri intercept eder (auth token, headers, logging vs.)
 */

import { HttpRequestConfig } from '../httpClient.interface';
import { AUTH_CONFIG } from '../../config/api.config';
import { store } from '../../../store'; // Redux store'unu import et

/**
 * Request interceptor interface
 */
export interface IRequestInterceptor {
  intercept(config: HttpRequestConfig): Promise<HttpRequestConfig>;
  getPriority(): number;
}

/**
 * Base request interceptor
 */
export abstract class BaseRequestInterceptor implements IRequestInterceptor {
  abstract intercept(config: HttpRequestConfig): Promise<HttpRequestConfig>;
  
  getPriority(): number {
    return 50; // Default priority
  }
}

/**
 * Auth token interceptor - JWT token'ı header'a ekler
 */
export class AuthTokenInterceptor extends BaseRequestInterceptor {
  getPriority(): number {
    return 100; // High priority - ilk çalışsın
  }

  async intercept(config: HttpRequestConfig): Promise<HttpRequestConfig> {
    // Eğer auth gerekmiyorsa veya token eklemeyi skip edeceksek
    if (config.requiresAuth === false || config.skipInterceptors) {
      return config;
    }

    // Token'ı Redux store'dan al
    const state = store.getState();
    const token = state.auth?.token || 
                  localStorage.getItem(AUTH_CONFIG.STORAGE_KEYS.ACCESS_TOKEN);

    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`
      };
    }

    return config;
  }
}

/**
 * Content-Type interceptor - Default content type ayarlar
 */
export class ContentTypeInterceptor extends BaseRequestInterceptor {
  getPriority(): number {
    return 90;
  }

  async intercept(config: HttpRequestConfig): Promise<HttpRequestConfig> {
    if (config.skipInterceptors) {
      return config;
    }

    // Eğer Content-Type header'ı yoksa ve data varsa
    if (!config.headers?.['Content-Type'] && config.data) {
      // FormData ise multipart/form-data yap
      if (config.data instanceof FormData) {
        config.headers = {
          ...config.headers,
          'Content-Type': 'multipart/form-data'
        };
      } 
      // String veya object ise application/json yap
      else if (typeof config.data === 'object' && config.data !== null) {
        config.headers = {
          ...config.headers,
          'Content-Type': 'application/json'
        };
      }
    }

    // Accept header'ını da ekle
    if (!config.headers?.['Accept']) {
      config.headers = {
        ...config.headers,
        'Accept': 'application/json'
      };
    }

    return config;
  }
}

/**
 * Request ID interceptor - Her request için unique ID ekler
 */
export class RequestIdInterceptor extends BaseRequestInterceptor {
  getPriority(): number {
    return 80;
  }

  async intercept(config: HttpRequestConfig): Promise<HttpRequestConfig> {
    if (config.skipInterceptors) {
      return config;
    }

    const requestId = this.generateRequestId();
    
    config.headers = {
      ...config.headers,
      'X-Request-ID': requestId,
      'X-Correlation-ID': requestId
    };

    // Request ID'yi config'e de kaydet (response'da kullanmak için)
    config.headers = {
      ...config.headers,
      'X-Request-ID': requestId
    };

    return config;
  }

  private generateRequestId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }
}

/**
 * CSRF token interceptor
 */
export class CsrfTokenInterceptor extends BaseRequestInterceptor {
  getPriority(): number {
    return 70;
  }

  async intercept(config: HttpRequestConfig): Promise<HttpRequestConfig> {
    if (config.skipInterceptors || config.method?.toLowerCase() === 'get') {
      return config;
    }

    // CSRF token'ını cookie'den veya localStorage'dan al
    const csrfToken = this.getCsrfToken();
    
    if (csrfToken) {
      config.headers = {
        ...config.headers,
        'X-CSRF-Token': csrfToken,
        'X-XSRF-Token': csrfToken
      };
    }

    return config;
  }

  private getCsrfToken(): string | null {
    // Cookie'den al
    const regex = new RegExp(/XSRF-TOKEN=([^;]+)/);
    const match = regex.exec(document.cookie);
    if (match) return decodeURIComponent(match[1]);
    
    // veya localStorage'dan al
    return localStorage.getItem('csrf_token');
  }
}

/**
 * Language interceptor - Dil header'ını ekler
 */
export class LanguageInterceptor extends BaseRequestInterceptor {
  getPriority(): number {
    return 60;
  }

  async intercept(config: HttpRequestConfig): Promise<HttpRequestConfig> {
    if (config.skipInterceptors) {
      return config;
    }

    // Mevcut dili al (i18n kütüphanesinden veya localStorage'dan)
    const language = localStorage.getItem('i18nextLng') || 
                     document.documentElement.lang || 
                     'tr';

    config.headers = {
      ...config.headers,
      'Accept-Language': language,
      'X-Language': language
    };

    return config;
  }
}

/**
 * Device info interceptor - Cihaz bilgilerini ekler
 */
export class DeviceInfoInterceptor extends BaseRequestInterceptor {
  getPriority(): number {
    return 50;
  }

  async intercept(config: HttpRequestConfig): Promise<HttpRequestConfig> {
    if (config.skipInterceptors) {
      return config;
    }

    const deviceInfo = this.getDeviceInfo();
    
    config.headers = {
      ...config.headers,
      'X-Device-ID': deviceInfo.deviceId,
      'X-Device-Type': deviceInfo.deviceType,
      'X-Platform': deviceInfo.platform,
      'X-App-Version': deviceInfo.appVersion
    };

    return config;
  }

  private getDeviceInfo() {
    return {
      deviceId: localStorage.getItem('device_id') || 'unknown',
      deviceType: this.getDeviceType(),
      platform: this.getPlatformInfo(),
      appVersion: import.meta.env.VITE_APP_VERSION || '1.0.0'
    };
  }

  private getPlatformInfo(): string {
    const ua = navigator.userAgent;
    
    // Sadece userAgent ile platform detection
    if (/Windows NT 10/.test(ua)) return 'Windows 10';
    if (/Windows NT 6\.3/.test(ua)) return 'Windows 8.1';
    if (/Windows NT 6\.2/.test(ua)) return 'Windows 8';
    if (/Windows NT 6\.1/.test(ua)) return 'Windows 7';
    if (/Windows/.test(ua)) return 'Windows';
    
    if (/Macintosh.*OS X 10_15/.test(ua)) return 'macOS Catalina';
    if (/Macintosh.*OS X 10_14/.test(ua)) return 'macOS Mojave';
    if (/Macintosh.*OS X/.test(ua)) return 'macOS';
    if (/Macintosh/.test(ua)) return 'Mac';
    
    if (/CrOS/.test(ua)) return 'Chrome OS';
    if (/Linux/.test(ua)) return 'Linux';
    if (/Android/.test(ua)) return 'Android';
    if (/iOS|iPhone|iPad|iPod/.test(ua)) return 'iOS';
    
    return 'Unknown';
  }

  private getDeviceType(): string {
    const ua = navigator.userAgent;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    const isTablet = /iPad|Android(?!.*Mobile)/i.test(ua);
    
    if (isTablet) return 'tablet';
    if (isMobile) return 'mobile';
    return 'desktop';
  }
}

/**
 * Logging interceptor - Request'leri loglar
 */
export class LoggingInterceptor extends BaseRequestInterceptor {
  getPriority(): number {
    return 10; // Low priority - son çalışsın
  }

  async intercept(config: HttpRequestConfig): Promise<HttpRequestConfig> {
    if (config.skipInterceptors || !this.shouldLog()) {
      return config;
    }
  
    const logData = {
      timestamp: new Date().toISOString(),
      method: config.method?.toUpperCase() || 'GET',
      url: config.url,
      headers: this.sanitizeHeaders(config.headers),
      data: this.sanitizeData(config.data),
      requiresAuth: config.requiresAuth
    };
  
    console.debug('[API Request]', logData);
  
    // Eğer logging sırasında config'i değiştirmek isterseniz:
    // Örneğin, log ID'si eklemek:
    const enhancedConfig = {
      ...config,
      _logId: this.generateLogId() // veya başka bir enhancement
    };
    
    return enhancedConfig;
  
    // Veya sadece logging yapacaksanız, void döndürebilirsiniz:
    // return config;
  }
  
  private generateLogId(): string {
    return `log_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  private shouldLog(): boolean {
    return import.meta.env.VITE_ENABLE_REQUEST_LOGGING === 'true' ||
           import.meta.env.NODE_ENV === 'development';
  }

  private sanitizeHeaders(headers: any): any {
    if (!headers) return {};
    
    const sanitized: any = { ...headers };
    
    // Sensitive headers'ı maskele
    if (sanitized.Authorization) {
      sanitized.Authorization = 'Bearer ***';
    }
    if (sanitized['X-CSRF-Token']) {
      sanitized['X-CSRF-Token'] = '***';
    }

    return sanitized;
  }

  private sanitizeData(data: any): any {
    if (!data) return data;
    
    if (data instanceof FormData) {
      return '[FormData]';
    }
    
    if (typeof data === 'object') {
      const sanitized = { ...data };
      
      // Sensitive fields'ı maskele
      const sensitiveFields = ['password', 'token', 'secret', 'key'];
      for (const field of sensitiveFields) {
        if (sanitized[field]) {
          sanitized[field] = '***';
        }
      }
      
      return sanitized;
    }
    
    return data;
  }
}

/**
 * Request Interceptor Manager - Tüm interceptor'ları yönetir
 */
export class RequestInterceptorManager {
  private interceptors: IRequestInterceptor[] = [];

  constructor() {
    // Default interceptor'ları ekle
    this.addInterceptor(new AuthTokenInterceptor());
    this.addInterceptor(new ContentTypeInterceptor());
    this.addInterceptor(new RequestIdInterceptor());
    this.addInterceptor(new CsrfTokenInterceptor());
    this.addInterceptor(new LanguageInterceptor());
    this.addInterceptor(new DeviceInfoInterceptor());
    this.addInterceptor(new LoggingInterceptor());
  }

  /**
   * Interceptor ekle
   */
  addInterceptor(interceptor: IRequestInterceptor): void {
    this.interceptors.push(interceptor);
    // Priority'ye göre sırala (yüksek priority önce)
    this.interceptors.sort((a, b) => b.getPriority() - a.getPriority());
  }

  /**
   * Interceptor kaldır
   */
  removeInterceptor(interceptor: IRequestInterceptor): void {
    const index = this.interceptors.indexOf(interceptor);
    if (index > -1) {
      this.interceptors.splice(index, 1);
    }
  }

  /**
   * Tüm interceptor'ları çalıştır
   */
  async intercept(config: HttpRequestConfig): Promise<HttpRequestConfig> {
    let processedConfig = { ...config };

    for (const interceptor of this.interceptors) {
      if (!processedConfig.skipInterceptors) {
        processedConfig = await interceptor.intercept(processedConfig);
      }
    }

    return processedConfig;
  }

  /**
   * Tüm interceptor'ları temizle
   */
  clear(): void {
    this.interceptors = [];
  }

  /**
   * Interceptor sayısını getir
   */
  getCount(): number {
    return this.interceptors.length;
  }
}

// Singleton instance
export const requestInterceptorManager = new RequestInterceptorManager();

// Default export
export default requestInterceptorManager;